import _ from 'lodash';
import template from './groups.html';

export default {
  name: 'groupComponent',
  template,
  controller: GroupController,
  controllerAs: 'GroupCtrl',
  bindings: {}
};

/* @ngInject */
function GroupController(
  $scope,
  $state,
  $stateParams,
  $transitions,
  growl,
  GroupService
) {
  const vm = this;

  // //////////////// //
  // Public Variables //
  // //////////////// //

  vm.pathToCurrentGroup = [];
  vm.treeOptions = {
    // callback function that is used to determine if any node has been moved to a different position
    accept(sourceNodeScope, destNodesScope) {
      if (destNodesScope.depth() === 0) {
        growl.warning('You cannot change the root group.', { title: 'Group Edit' });
        return false;
      }

      // get the organization of the destination node and the parent of the source node
      const destNodeOrg = destNodesScope.$nodeScope.$modelValue?.org;
      const sourceNodeParentOrg = sourceNodeScope.$parentNodeScope.$modelValue?.org;
      // if the destination node has no organization or the source node's parent has no organization
      // or the destination node's organization is different from the source node's parent organization
      // then the move is not allowed
      if (!(destNodeOrg && sourceNodeParentOrg) || destNodeOrg !== sourceNodeParentOrg) {
        growl.warning('You cannot move a group to a new organization.', { title: 'Group Edit' });
        return false;
      }
      return true;
    },
    beforeDrag(sourceNodeScope) {
      if (sourceNodeScope.depth() === 1) {
        growl.warning('You cannot move the root group.', { title: 'Group Edit' });
        return false;
      }
      return true;
    },
    dropped(event) {
      const source = event.source.nodeScope.$modelValue;
      const parent = event.dest.nodesScope.$nodeScope.$modelValue;

      if (source.parent === parent.id) {
        return;
      }

      const growlMessage = growl.info(`Moving ${source.name}...`);
      return GroupService.editGroupParent(source.id, { parent: parent.id })
        .then(() => {
          growl.success(`${source.name} moved`);
          source.parent = parent.id;
        })
        .catch(err => growl.error(err.error || err, { title: 'Error Saving Group:' }))
        .then(() => selectGroup().then(() => selectGroup(source.id)))
        .finally(() => {
          growlMessage.destroy();
        });
    }
  };

  vm.editingGroup = false;

  // hides options if user doesn't have access
  vm.userAuth = {
    canEditName: true,
    canRemoveGroup: true,
    canAddGroup: true,
    canAssignUser: true,
    canCreateUser: true,
    canRemoveUser: true,
    canDeleteUser: true
  };


  const transitions = [
    $transitions.onSuccess(
      { to: 'admin.groups.group' },
      transition => setActiveGroup(transition.targetState().params().id)
    ),
  ];

  // //////////////// //
  // Public Functions //
  // //////////////// //
  vm.$onInit = $onInit;
  vm.$onDestroy = () => transitions.forEach(deregister => deregister());
  vm.selectGroup = selectGroup;
  vm.editNameToggle = editNameToggle;
  vm.submitNewName = submitNewName;
  vm.toggleUsers = toggleUsers;
  vm.toggleDashboards = toggleDashboards;
  vm.toggleSites = toggleSites;
  vm.saveGroupPermissions = saveGroupPermissions;

  function $onInit() {
    GroupService.getGroupTree().then(({ data: tree }) => {
      vm.groupTree = tree;
      setActiveGroup($stateParams.id);
    });
  }

  // makes the group selected on the group tree the currentGroup
  function selectGroup(id) {
    return id ?
      $state.go('admin.groups.group', { id }) :
      $state.go('admin.groups');
  }

  /**
   * @private (!) Do not call this directly - use `selectGroup` (router) instead
   * @param {string} [id]
   * @returns {Promise<undefined>}
   */
  async function setActiveGroup(id) {
    if (!id) {
      // Clear
      vm.currentId = undefined;
      vm.currentGroup = undefined;
      vm.pathToCurrentGroup = [];
      return;
    }

    try {
      vm.currentGroup = _.find(await GroupService.getGroups(vm.groupTree), { id });
      const groupIsPopulated = _.has(vm.currentGroup, 'users');
      // it is assumed that a group that has users also contains dashboards and sites
      if (!groupIsPopulated) {
        /*
            It is imperative to make at least 1 call to the API to retrieve the group
            in order to display information about associated documents (users, sites, ec).
            This is preferred to modifying the tree structure in the API's response to include
            the needed documents since it would introduce a non-negligible burden on the browser.
          */
        _.assign(vm.currentGroup, await GroupService.getGroup(id));
      }
      _.defaults(vm.currentGroup, { permissions: {} });

      vm.currentId = id;
      vm.editingGroup = false;
      vm.pathToCurrentGroup = await GroupService.findPathTo(vm.currentGroup, vm.groupTree);
      $scope.$apply();
    } catch (err) {
      // Should only happen if client has poor connectivity
      growl.error(
        'We were unable to retrieve your groups. You may need to refresh the page and try again',
        { title: 'Oops! Something went wrong' }
      );
      console.error(err.error ?? err);
      selectGroup();
    }
  }

  let editedGroupName;
  function editNameToggle() {
    if (vm.editingGroup) {
      // Restore name since input field is ng-model
      vm.currentGroup.name = editedGroupName;
    } else {
      editedGroupName = vm.currentGroup.name;
    }
    vm.editingGroup = !vm.editingGroup;
  }

  function submitNewName() {
    let growlMessage = growl.info('Saving name...', { disableCloseButton: true });
    const newName = { name: vm.currentGroup.name };
    const id = vm.currentGroup.id;
    GroupService.editGroup(id, newName)
      .then(() => {
        growlMessage.destroy();
        growlMessage = growl.success(`${editedGroupName} renamed to ${newName.name}`);
        vm.editingGroup = false;
      })
      .catch((res) => {
        growlMessage.destroy();
        growlMessage = growl.error(res.error, { title: 'Error Saving Name:' });
        editNameToggle();
      });
  }

  function toggleUsers() {
    vm.usersCollapsed = !vm.usersCollapsed;
  }

  function toggleDashboards() {
    vm.dashboardsCollapsed = !vm.dashboardsCollapsed;
  }

  function toggleSites() {
    vm.sitesCollapsed = !vm.sitesCollapsed;
  }

  function hasBlankPermission(permissions) {
    for (const level of Object.values(permissions)) {
      for (const loc of Object.values(level)) {
        for (const comp of Object.values(loc)) {
          if (_.some(comp, val => !val)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  function saveGroupPermissions($event) {
    if (hasBlankPermission($event.permissions)) {
      return growl.error('Cannot save blank permissions...');
    }
    // $event save
    let growlMessage = growl.info('Saving permissions...', { disableCloseButton: true });
    const id = vm.currentGroup.id;
    return GroupService.editPermissionLevels(id, $event)
      .then(() => {
        growlMessage.destroy();
        growlMessage = growl.success('Permissions saved');
        vm.currentGroup.permissions = $event.permissions;
      })
      .catch((res) => {
        growlMessage.destroy();
        growlMessage = growl.error(res.error, { title: 'Error Saving Permissions:' });
      });
  }
}
